import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const MyPromptForm = ({ prompt, onSaved, onCancel }) => {
  const isNew = !prompt.id;
  const [form, setForm] = useState({ name: prompt.name || "", description: prompt.description || "", prompt: prompt.prompt || "" });
  const [brief, setBrief] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (brief.trim().length < 10) return toast.error("Describe what you want the method to do (a sentence or two)");
    setGenerating(true);
    try {
      const { data } = await api.post("/inquiry-prompts/generate", { brief: brief.trim() });
      set("prompt", data.prompt);
      toast.success("Method generated — read it over and tweak anything you like");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate the method");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Give your method a name");
    if (form.prompt.trim().length < 20) return toast.error("The method text is too short — write it or generate it first");
    setSaving(true);
    try {
      const { data } = isNew
        ? await api.post("/inquiry-prompts/mine", form)
        : await api.put(`/inquiry-prompts/mine/${prompt.id}`, form);
      toast.success(`"${data.name}" saved to your methods`);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the method");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground";
  const inputCls = "mt-2 bg-background border-border rounded-sm focus-visible:ring-[#007AFF]";

  return (
    <div className="space-y-4" data-testid="my-prompt-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className={labelCls}>Name</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="my-prompt-name-input"
            placeholder="My retail returns method" className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>Short description</Label>
          <Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="my-prompt-description-input"
            placeholder="When to use this one" className={inputCls} />
        </div>
      </div>

      <div className="border border-[#007AFF]/30 bg-[#007AFF]/5 p-4">
        <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] flex items-center gap-2">
          <Wand2 className="w-3.5 h-3.5" /> Generate with AI
        </Label>
        <p className="text-[11px] text-muted-foreground mt-1 mb-2">
          Describe the situations you deal with and the tone you want. I'll write the full method for you to tailor.
        </p>
        <div className="flex gap-2">
          <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2} data-testid="my-prompt-brief-input"
            placeholder='e.g. "Online retail orders that arrive damaged. Polite but firm. Always ask who packed and inspected it."'
            className="bg-background border-border rounded-sm text-xs resize-y focus-visible:ring-[#007AFF]" />
          <Button type="button" size="sm" variant="outline" onClick={generate} disabled={generating} data-testid="my-prompt-generate-btn"
            className="rounded-sm gap-1.5 h-auto text-xs border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 hover:text-[#007AFF] shrink-0">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {generating ? "Writing…" : "Generate"}
          </Button>
        </div>
      </div>

      <div>
        <Label className={labelCls}>Method text (the AI's instructions)</Label>
        <Textarea value={form.prompt} onChange={(e) => set("prompt", e.target.value)} data-testid="my-prompt-text-input"
          rows={12} placeholder="Write your own instructions, or generate a starting point above."
          className={`${inputCls} font-mono text-xs leading-relaxed resize-y`} />
        <p className="text-[10px] text-muted-foreground font-mono mt-1">{form.prompt.length.toLocaleString()} / 20,000 chars</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} className="rounded-sm" data-testid="my-prompt-cancel-btn">Cancel</Button>
        <Button type="button" onClick={save} disabled={saving} data-testid="my-prompt-save-btn"
          className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : isNew ? "Save method" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};
